// ─────────────────────────────────────────────────────────────────────────────
// Geometría de geocercas para el análisis de entregas (point-in-polygon en el
// navegador sobre los datasets de public/data/geofences).
//
// El punto caliente es `countDeliveriesByCommune`: decenas de miles de entregas
// contra los polígonos de una región. Con el enfoque ingenuo (bbox por feature +
// ray-casting sobre TODAS las aristas del anillo) el costo es
// O(puntos × features × vértices) y explota en las regiones australes, que son
// archipiélagos con cientos de miles de vértices: medido con 50.000 coordenadas
// sintéticas, Magallanes tardaba ~85 s y Aysén ~53 s — la pestaña se congelaba.
//
// La estructura que lo resuelve tiene dos niveles, ambos construidos una sola vez
// por objeto GeoJSON y cacheados en WeakMap (el objeto lo mantiene vivo
// `utils/geofences.js`, que cachea el fetch por región):
//
//  1. PARTES. Cada feature se descompone en polígonos individuales (una isla =
//     una parte), cada uno con su propio bbox. El bbox de un MultiPolygon abarca
//     todo el archipiélago y no descarta nada; el bbox de cada isla sí.
//  2. BANDAS DE LATITUD por anillo. El ray-casting es horizontal: para un punto
//     en latitud `lat` solo pueden cruzar el rayo las aristas cuyo rango de
//     latitud contiene `lat`. Indexando las aristas en bandas horizontales, el
//     test pasa de recorrer N aristas a recorrer las ~16 de una banda.
//
// El mismo índice sirve a `pointInPolygon`/`pointInFeature`, así que hay UNA sola
// implementación del test de cruce (sin riesgo de divergencia entre el camino
// rápido y el camino "simple").
// ─────────────────────────────────────────────────────────────────────────────

// ── Caches (WeakMap: se liberan solos cuando el GeoJSON deja de referenciarse) ─

/** anillo (Array<[lng,lat]>) → índice de bandas */
const ringIndexCache = new WeakMap();
/** geometry → partes (polígonos individuales con bbox y anillos indexados) */
const partsCache = new WeakMap();
/** geometry → bounds en formato Leaflet */
const boundsCache = new WeakMap();
/** FeatureCollection → [{ name, part }] aplanado */
const communeIndexCache = new WeakMap();

// Una banda por cada ~16 aristas: suficiente para que el barrido por banda sea
// corto sin inflar el índice. El tope evita degenerar en anillos gigantes.
const EDGES_PER_BAND = 16;
const MAX_BANDS = 4096;
// Por debajo de este número de vértices el índice cuesta más que el barrido.
const MIN_VERTICES_FOR_BANDS = 32;

/**
 * Construye el índice de un anillo: bbox + aristas agrupadas en bandas de
 * latitud. Las aristas horizontales se descartan: nunca satisfacen la condición
 * de cruce `(yi > lat) !== (yj > lat)`.
 *
 * Todo se guarda en arrays tipados y NO se vuelve a tocar el array anidado de
 * GeoJSON. Es la diferencia entre 490 ms y 36 ms en Aysén (50.000 puntos):
 * recorrer `ring[i][0]` obliga a un salto de puntero por vértice hacia objetos
 * dispersos en el heap, y el costo real del barrido eran los fallos de caché,
 * no la aritmética. Las aristas de cada banda van en un único Int32Array con
 * offsets (formato CSR) en vez de un array por banda: menos objetos que asignar
 * al construir y una lectura contigua al consultar.
 *
 * @param {Array<[number, number]>} ring vértices GeoJSON [lng, lat]
 */
function buildRingIndex(ring) {
  const n = ring.length;
  const xy = new Float64Array(n * 2);
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (let i = 0; i < n; i += 1) {
    const lng = ring[i][0];
    const lat = ring[i][1];
    xy[i * 2] = lng;
    xy[i * 2 + 1] = lat;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  let latSpanSum = 0;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    latSpanSum += Math.abs(xy[i * 2 + 1] - xy[j * 2 + 1]);
  }

  const latRange = maxLat - minLat;
  let bandCount = 1;
  if (n >= MIN_VERTICES_FOR_BANDS && latRange > 0) {
    const byDensity = Math.ceil(n / EDGES_PER_BAND);
    // Bandas más finas que la arista promedio no reducen el barrido: solo hacen
    // que cada arista se registre en varias bandas.
    const meanSpan = latSpanSum / n;
    const bySpan = meanSpan > 0 ? Math.floor(latRange / meanSpan) : byDensity;
    bandCount = Math.max(1, Math.min(byDensity, bySpan || byDensity, MAX_BANDS));
  }
  const bandHeight = bandCount > 1 ? latRange / bandCount : 0;

  // Con una sola banda el índice no filtra nada: se ahorra construirlo (son
  // ~2.300 de los 2.629 anillos de Magallanes — islotes de pocos vértices) y la
  // consulta barre el anillo entero, que es lo mismo que haría la banda única.
  if (bandCount === 1) {
    return {
      xy, n, minLat, maxLat, minLng, maxLng,
      bandCount, bandHeight, bandStart: null, bandEdges: null,
    };
  }

  const bandOf = (lat) => {
    const b = Math.floor((lat - minLat) / bandHeight);
    return b < 0 ? 0 : b >= bandCount ? bandCount - 1 : b;
  };

  // Pasada 1: cuántas aristas cae en cada banda.
  const bandStart = new Int32Array(bandCount + 1);
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = xy[i * 2 + 1];
    const yj = xy[j * 2 + 1];
    if (yi === yj) continue; // arista horizontal: nunca cruza el rayo
    const from = bandOf(yi < yj ? yi : yj);
    const to = bandOf(yi < yj ? yj : yi);
    for (let b = from; b <= to; b += 1) bandStart[b + 1] += 1;
  }
  // Suma de prefijos → offset inicial de cada banda.
  for (let b = 0; b < bandCount; b += 1) bandStart[b + 1] += bandStart[b];

  // Pasada 2: relleno.
  const bandEdges = new Int32Array(bandStart[bandCount]);
  const cursor = bandStart.slice(0, bandCount);
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = xy[i * 2 + 1];
    const yj = xy[j * 2 + 1];
    if (yi === yj) continue;
    const from = bandOf(yi < yj ? yi : yj);
    const to = bandOf(yi < yj ? yj : yi);
    for (let b = from; b <= to; b += 1) {
      bandEdges[cursor[b]] = i;
      cursor[b] += 1;
    }
  }

  return { xy, n, minLat, maxLat, minLng, maxLng, bandCount, bandHeight, bandStart, bandEdges };
}

function ringIndexFor(ring) {
  let idx = ringIndexCache.get(ring);
  if (!idx) {
    idx = buildRingIndex(ring);
    ringIndexCache.set(ring, idx);
  }
  return idx;
}

/**
 * ¿La arista (i, j) del anillo cruza el rayo horizontal que sale del punto hacia
 * el este? Fórmula única para los dos recorridos de `pointInRingIndex` (barrido
 * completo e indexado por banda); `i2`/`j2` son offsets ya multiplicados en `xy`.
 */
function edgeCrossesRay(xy, i2, j2, lat, lng) {
  const yi = xy[i2 + 1];
  const yj = xy[j2 + 1];
  if ((yi > lat) === (yj > lat)) return false;
  const xi = xy[i2];
  const xj = xy[j2];
  return lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
}

/**
 * Ray-casting (crossing number) sobre un anillo ya indexado. El descarte por
 * bbox es válido para anillos cerrados: un rayo horizontal que pasa por fuera
 * del bbox cruza el anillo cero o un número par de veces.
 */
function pointInRingIndex(lat, lng, idx) {
  if (lat < idx.minLat || lat > idx.maxLat || lng < idx.minLng || lng > idx.maxLng) {
    return false;
  }
  const { xy, n, bandStart, bandEdges, bandCount, bandHeight, minLat } = idx;
  let inside = false;

  if (bandEdges === null) {
    for (let i = 0, j = n - 1; i < n; j = i++) {
      if (edgeCrossesRay(xy, i * 2, j * 2, lat, lng)) inside = !inside;
    }
    return inside;
  }

  let band = Math.floor((lat - minLat) / bandHeight);
  if (band < 0) band = 0;
  else if (band >= bandCount) band = bandCount - 1;

  const to = bandStart[band + 1];
  const last = n - 1;
  for (let k = bandStart[band]; k < to; k += 1) {
    const i = bandEdges[k];
    const j = i === 0 ? last : i - 1;
    if (edgeCrossesRay(xy, i * 2, j * 2, lat, lng)) inside = !inside;
  }
  return inside;
}

/**
 * Una "parte" es un polígono simple: anillo exterior + sus anillos interiores
 * (holes), con el bbox del exterior.
 */
function buildPart(rings) {
  // El anillo exterior tiene que ser válido POR SÍ MISMO: si se filtrara junto
  // con los interiores, un exterior degenerado ascendería un hole a exterior y
  // el polígono quedaría invertido.
  if (!Array.isArray(rings) || !Array.isArray(rings[0]) || rings[0].length < 3) {
    return null;
  }
  const indexed = rings.filter((r) => Array.isArray(r) && r.length >= 3).map(ringIndexFor);
  const outer = indexed[0];
  return {
    rings: indexed,
    minLat: outer.minLat,
    maxLat: outer.maxLat,
    minLng: outer.minLng,
    maxLng: outer.maxLng,
  };
}

/** Un punto pertenece a la parte si cae en el exterior y en ningún hole. */
function pointInPart(lat, lng, part) {
  if (lat < part.minLat || lat > part.maxLat || lng < part.minLng || lng > part.maxLng) {
    return false;
  }
  if (!pointInRingIndex(lat, lng, part.rings[0])) return false;
  for (let h = 1; h < part.rings.length; h += 1) {
    if (pointInRingIndex(lat, lng, part.rings[h])) return false;
  }
  return true;
}

/** Partes de una geometría GeoJSON (Polygon → 1; MultiPolygon → N). Cacheado. */
function polygonParts(geometry) {
  if (!geometry) return [];
  let parts = partsCache.get(geometry);
  if (parts) return parts;

  const { type, coordinates } = geometry;
  if (type === 'Polygon') {
    const part = buildPart(coordinates);
    parts = part ? [part] : [];
  } else if (type === 'MultiPolygon') {
    parts = (coordinates || []).map(buildPart).filter(Boolean);
  } else {
    parts = [];
  }

  partsCache.set(geometry, parts);
  return parts;
}

/**
 * Bounding box de una geometría GeoJSON (Polygon o MultiPolygon).
 * @returns {[[number, number], [number, number]] | null}
 *   `[[minLat, minLng], [maxLat, maxLng]]` — formato Leaflet `fitBounds`.
 */
export function getBounds(geometry) {
  if (!geometry) return null;
  const cached = boundsCache.get(geometry);
  if (cached !== undefined) return cached;

  const parts = polygonParts(geometry);
  let bounds = null;
  if (parts.length) {
    let minLat = Infinity;
    let minLng = Infinity;
    let maxLat = -Infinity;
    let maxLng = -Infinity;
    for (const p of parts) {
      if (p.minLat < minLat) minLat = p.minLat;
      if (p.minLng < minLng) minLng = p.minLng;
      if (p.maxLat > maxLat) maxLat = p.maxLat;
      if (p.maxLng > maxLng) maxLng = p.maxLng;
    }
    bounds = [
      [minLat, minLng],
      [maxLat, maxLng],
    ];
  }

  boundsCache.set(geometry, bounds);
  return bounds;
}

/**
 * Normaliza un string para comparación: minúsculas, sin tildes, con apóstrofos
 * tipográficos unificados al ASCII y espacios colapsados.
 *
 * Los apóstrofos importan: "O'Higgins" viaja como U+2019 desde los geocoders y
 * como U+0027 desde los formularios, y sin unificarlos el mismo nombre no
 * matchea consigo mismo.
 *
 * @param {string} str
 * @returns {string}
 */
export function normalizeString(str) {
  return String(str ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2018\u2019\u02bc]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Interpola un color RGB entre dos extremos según un valor normalizado [0, 1].
 * Retorna [r, g, b, a] para deck.gl.
 */
export function interpolateColor(value) {
  // Gradiente: azul (frío) → naranja → rojo (caliente)
  const stops = [
    { v: 0,    r: 59,  g: 130, b: 246, a: 180 }, // blue-500
    { v: 0.25, r: 34,  g: 197, b: 94,  a: 200 }, // green-500
    { v: 0.5,  r: 234, g: 179, b: 8,   a: 210 }, // yellow-500
    { v: 0.75, r: 249, g: 115, b: 22,  a: 220 }, // orange-500
    { v: 1,    r: 239, g: 68,  b: 68,  a: 230 }, // red-500
  ];

  const clamped = Math.max(0, Math.min(1, value));

  let lower = stops[0];
  let upper = stops[stops.length - 1];

  for (let i = 0; i < stops.length - 1; i++) {
    if (clamped >= stops[i].v && clamped <= stops[i + 1].v) {
      lower = stops[i];
      upper = stops[i + 1];
      break;
    }
  }

  const t = upper.v === lower.v ? 0 : (clamped - lower.v) / (upper.v - lower.v);
  return [
    Math.round(lower.r + t * (upper.r - lower.r)),
    Math.round(lower.g + t * (upper.g - lower.g)),
    Math.round(lower.b + t * (upper.b - lower.b)),
    Math.round(lower.a + t * (upper.a - lower.a)),
  ];
}

/**
 * ¿El punto (lat, lng) cae dentro de un anillo GeoJSON?
 *
 * Es el test de UN anillo: no sabe de holes. Para un polígono completo usar
 * `pointInFeature`.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {Array<[number, number]>} ring vértices [[lng, lat], ...]
 * @returns {boolean}
 */
export function pointInPolygon(lat, lng, ring) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  return pointInRingIndex(lat, lng, ringIndexFor(ring));
}

/**
 * ¿El punto (lat, lng) cae dentro de un Feature GeoJSON (Polygon o
 * MultiPolygon)? Respeta los anillos interiores: un punto en un hole está FUERA.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {object} feature GeoJSON Feature
 * @returns {boolean}
 */
export function pointInFeature(lat, lng, feature) {
  const parts = polygonParts(feature?.geometry);
  for (let i = 0; i < parts.length; i += 1) {
    if (pointInPart(lat, lng, parts[i])) return true;
  }
  return false;
}

// Grid hash: solo vale la pena a partir de unas pocas partes.
const MIN_PARTS_FOR_GRID = 8;
const MAX_GRID_SIDE = 128;
// Tope de inserciones (una parte se registra en cada celda que toca su bbox).
// Con partes muy grandes una grilla fina degenera; el constructor la va
// engrosando hasta caer bajo este techo.
const gridBudget = (parts) => 16 * parts + 4096;

/**
 * Grilla uniforme sobre el bbox del conjunto de partes: celda → índices de las
 * partes cuyo bbox la toca. Reduce el descarte por bbox de O(partes) a O(1)
 * lookup + las pocas candidatas de la celda.
 *
 * Sin esto, Magallanes (2.495 partes) obligaba a 50.000 × 2.495 comparaciones de
 * bbox por consulta, que era el costo dominante una vez indexadas las aristas.
 */
function buildGrid(parts) {
  if (parts.length < MIN_PARTS_FOR_GRID) return null;

  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  for (const p of parts) {
    if (p.minLat < minLat) minLat = p.minLat;
    if (p.minLng < minLng) minLng = p.minLng;
    if (p.maxLat > maxLat) maxLat = p.maxLat;
    if (p.maxLng > maxLng) maxLng = p.maxLng;
  }
  const latRange = maxLat - minLat;
  const lngRange = maxLng - minLng;
  if (!(latRange > 0) || !(lngRange > 0)) return null;

  let side = Math.min(MAX_GRID_SIDE, Math.max(2, Math.ceil(Math.sqrt(parts.length))));
  const budget = gridBudget(parts.length);

  // Rangos de celda por parte, recalculados al engrosar la grilla.
  const spans = new Array(parts.length);
  const computeSpans = () => {
    const cellLat = latRange / side;
    const cellLng = lngRange / side;
    let total = 0;
    for (let i = 0; i < parts.length; i += 1) {
      const p = parts[i];
      const r0 = Math.min(side - 1, Math.max(0, Math.floor((p.minLat - minLat) / cellLat)));
      const r1 = Math.min(side - 1, Math.max(0, Math.floor((p.maxLat - minLat) / cellLat)));
      const c0 = Math.min(side - 1, Math.max(0, Math.floor((p.minLng - minLng) / cellLng)));
      const c1 = Math.min(side - 1, Math.max(0, Math.floor((p.maxLng - minLng) / cellLng)));
      spans[i] = [r0, r1, c0, c1];
      total += (r1 - r0 + 1) * (c1 - c0 + 1);
    }
    return total;
  };

  while (computeSpans() > budget && side > 4) side = Math.max(4, side >> 1);

  const cells = new Array(side * side);
  for (let i = 0; i < parts.length; i += 1) {
    const [r0, r1, c0, c1] = spans[i];
    for (let r = r0; r <= r1; r += 1) {
      for (let c = c0; c <= c1; c += 1) {
        const k = r * side + c;
        // Las partes se insertan en orden creciente, así que cada celda queda
        // ordenada: "gana la primera que contiene el punto" se preserva.
        if (cells[k]) cells[k].push(i);
        else cells[k] = [i];
      }
    }
  }

  return {
    minLat,
    minLng,
    side,
    cellLat: latRange / side,
    cellLng: lngRange / side,
    maxLat,
    maxLng,
    cells,
  };
}

/** Índices de partes candidatas para un punto, o null si está fuera de la grilla. */
function gridCandidates(grid, lat, lng) {
  if (lat < grid.minLat || lat > grid.maxLat || lng < grid.minLng || lng > grid.maxLng) {
    return null;
  }
  let r = Math.floor((lat - grid.minLat) / grid.cellLat);
  let c = Math.floor((lng - grid.minLng) / grid.cellLng);
  if (r < 0) r = 0; else if (r >= grid.side) r = grid.side - 1;
  if (c < 0) c = 0; else if (c >= grid.side) c = grid.side - 1;
  return grid.cells[r * grid.side + c] || null;
}

/**
 * Índice de una FeatureCollection de geocercas: partes aplanadas con su nombre
 * + grilla espacial. `name` sale de `properties.name` (contrato uniforme de los
 * datasets de public/data/geofences, §3.3 del plan de expansión).
 */
function communeIndex(geojson) {
  let index = communeIndexCache.get(geojson);
  if (index) return index;

  const names = [];
  const parts = [];
  for (const feature of geojson.features) {
    const name = feature?.properties?.name;
    if (!name) continue;
    for (const part of polygonParts(feature.geometry)) {
      names.push(name);
      parts.push(part);
    }
  }

  index = { names, parts, grid: buildGrid(parts) };
  communeIndexCache.set(geojson, index);
  return index;
}

/**
 * Asigna cada coordenada de entrega a la comuna/municipio que la contiene y
 * devuelve el conteo por nombre, de mayor a menor.
 *
 * @param {Array<{latitude: number, longitude: number}>} coords
 * @param {object} geojson FeatureCollection de una región (public/data/geofences)
 * @returns {Array<{comuna: string, count: number}>}
 */
export function countDeliveriesByCommune(coords, geojson) {
  if (!coords?.length || !geojson?.features?.length) return [];

  const { names, parts, grid } = communeIndex(geojson);
  if (!parts.length) return [];

  const countMap = new Map();
  const all = grid ? null : parts.map((_, i) => i);

  for (let c = 0; c < coords.length; c += 1) {
    const { latitude, longitude } = coords[c];
    if (latitude == null || longitude == null) continue;

    const candidates = grid ? gridCandidates(grid, latitude, longitude) : all;
    if (!candidates) continue;

    for (let k = 0; k < candidates.length; k += 1) {
      const i = candidates[k];
      const part = parts[i];
      // Descarte por bbox de la PARTE (no del feature): en un archipiélago el
      // bbox del feature abarca toda la región y no descartaría nada.
      if (
        latitude < part.minLat || latitude > part.maxLat ||
        longitude < part.minLng || longitude > part.maxLng
      ) continue;

      if (pointInPart(latitude, longitude, part)) {
        countMap.set(names[i], (countMap.get(names[i]) || 0) + 1);
        break; // una entrega pertenece a una sola comuna
      }
    }
  }

  return Array.from(countMap, ([comuna, count]) => ({ comuna, count })).sort(
    (a, b) => b.count - a.count,
  );
}
